"use client";

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Header from "../components/Header";
import Filters, { FiltersState } from "../components/Filters";
import ListingCard from "../components/ListingCard";
import {
  getListingsCache,
  saveListings,
  getListingsPaginatedAsync,
  getListingsCountAsync,
  ListingsQueryResult,
} from "../lib/storage";
import { Listing } from "../lib/types";
import { seedIfEmpty } from "../lib/seed";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { ListingGridSkeleton } from "../components/LoadingSkeleton";
import { CITIES_BIH } from "../lib/cities";
import { trackEvent } from "../lib/analytics";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { isAdmin } from "../lib/auth";

// Default options for filters
const DEFAULT_SIZES = [
  "XS", "S", "M", "L", "XL", "XXL", "XXXL",
  // Dječija odjeća (EU visine i klasične dječije veličine)
  "50", "56", "62", "68", "74", "80", "86", "92", "98",
  "104", "110", "116", "122", "128", "134", "140", "146",
  "152", "158", "164", "170", "176",
  "0-3 mjeseca", "3-6 mjeseci", "6-9 mjeseci", "9-12 mjeseci",
  "12-18 mjeseci", "18-24 mjeseca",
  "2-3 godine", "3-4 godine", "4-5 godina", "5-6 godina",
  "6-7 godina", "7-8 godina", "8-9 godina", "9-10 godina",
  "10-11 godina", "11-12 godina", "12-13 godina", "13-14 godina", "14-15 godina",
  // Odrasle / univerzalne veličine
  "28", "30", "32", "34", "35", "36", "37", "38", "39", "40",
  "41", "42", "43", "44", "45", "46", "48", "52",
  "Jedna veličina", "Univerzalna veličina"
];
const DEFAULT_COLORS = [
  "Crna", "Bijela", "Siva", "Bež", "Crvena", "Bordo", "Plava", "Zelena", "Roza", "Smeđa",
  "Narandžasta", "Žuta", "Ljubičasta", "Tirkizna", "Koralna", "Breskva", "Menta", "Krem",
  "Crvenkasta", "Tamno plava", "Svijetlo plava", "Tamno siva", "Svijetlo siva",
  "Zlatna", "Srebrna", "Bronzana", "Multicolor", "Print", "Prugasta", "Točkasta"
];
const DEFAULT_TYPES = [
  "Abaja", "Aksesuar", "Baletanke", "Bermuda", "Bluza", "Bodysuit",
  "Čizme", "Čizme do koljena", "Cipele",
  "Dugačke hlače", "Duks", "Džemper", "Džilbab",
  "Farmerke",
  "Haljina", "Hidžab", "Hlače",
  "Jakna", "Jeans",
  "Kaftan", "Kaput", "Kardigan", "Kapa", "Kimono", "Kladionica", "Kombinezon", "Košulja", "Kratke hlače",
  "Lanac", "Leggings", "Loafers",
  "Majica", "Mokasine",
  "Naočare", "Narukvica", "Naušnice", "Niqab", "Novčanik",
  "Odijelo",
  "Pantalone", "Patike", "Pidžama", "Portfelj", "Prsten", "Pulover",
  "Ranac", "Rukavice", "Ruksak",
  "Sako", "Sandale", "Šal", "Šešir", "Šorc", "Suknja",
  "Top", "Torba", "Torbica", "Trenerica", "Trenerka", "Tunika",
  "Vesta"
];
const DEFAULT_BRANDS = [
  "Zara", "Bershka", "Stradivarius", "Pull&Bear", "H&M", "Mango", "Valentino", "Nike", "Adidas", "Puma", "Massimo Dutti",
  "Calvin Klein", "Tommy Hilfiger", "Ralph Lauren", "Guess", "Levi's", "Diesel", "Armani", "Hugo Boss",
  "Lacoste", "Polo", "Vans", "Converse", "New Balance", "Reebok", "Fila", "Champion",
  "Reserved", "Mohito", "Sinsay", "House", "C&A", "Peek & Cloppenburg"
];

// Manji batch za brži prvi load (kao prije); više se učitava infinite scrollom
const PAGE_SIZE = 8;
const FETCH_BATCH_SIZE = 12;

const EMPTY: FiltersState = {
  q: "",
  category: "",
  productType: "",
  brand: "",
  size: "",
  color: "",
  condition: "",
  city: "",
  min: "",
  max: "",
};

type SortOption =
  | "newest"
  | "oldest"
  | "price-low"
  | "price-high"
  | "trade"
  | "offers";

// Custom dropdown component for mobile
function MobileSortDropdown({ 
  value, 
  onChange 
}: { 
  value: SortOption; 
  onChange: (value: SortOption) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const options: { value: SortOption; label: string }[] = [
    { value: "newest", label: "Najnovije" },
    { value: "oldest", label: "Najstarije" },
    { value: "price-low", label: "Najniža cijena" },
    { value: "price-high", label: "Najviša cijena" },
    { value: "trade", label: "Samo zamjena" },
    { value: "offers", label: "Samo ponuda" },
  ];

  const selectedOption = options.find(opt => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] text-[var(--text)] text-[11px] flex items-center justify-between"
      >
        <span>{selectedOption?.label || "Najnovije"}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-[var(--card)] border border-[var(--border)] rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-[11px] hover:bg-[var(--bg)] transition first:rounded-t-lg last:rounded-b-lg ${
                value === option.value
                  ? 'bg-[var(--accent)]/10 text-[var(--accent)] font-semibold'
                  : 'text-[var(--text)]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [filters, setFilters] = useState<FiltersState>(EMPTY);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [listingBuffer, setListingBuffer] = useState<Listing[]>([]); // Buffer for extra fetched listings
  const [totalCount, setTotalCount] = useState<number | null>(null); // Total count of listings (null if unknown)
  const [isMobilePerfMode, setIsMobilePerfMode] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const isFirstSearchRef = useRef(true); // da izbjegnemo dupli upit na inicijalnom učitavanju
  const isFirstFiltersRef = useRef(true);
  const hasRestoredScrollRef = useRef(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const processedListingsCountRef = useRef(0);
  const firstListingIdRef = useRef<string>("");

  // Load initial listings. Ako je backgroundRefresh true, ne prikazuj loader (koristi se kad već imamo cache).
  const loadListings = useCallback(async (reset: boolean = false, backgroundRefresh: boolean = false) => {
    if (reset) {
      setLastDoc(null);
      setHasMore(true);
      if (!backgroundRefresh) setLoading(true);
      setListingBuffer([]);
      setTotalCount(null);
    } else {
      setLoadingMore(true);
    }

    try {
      // Na public stranici marketa ne zavisimo od Firebase autentikacije
      const userIdToExclude = undefined;
      let minPrice = filters.min ? Number(filters.min) : undefined;
      const maxPrice = filters.max ? Number(filters.max) : undefined;
      // Kad sortiraš po cijeni, dohvati samo oglase sa stvarnom cijenom (isključi "po dogovoru")
      if (sortBy === "price-low" || sortBy === "price-high") {
        const userMin = minPrice ?? 0;
        minPrice = userMin > 0 ? userMin : 0.01;
      }

      const onlyTrade = sortBy === "trade";
      const onlyOffers = sortBy === "offers";
      const sortParam =
        sortBy === "trade" || sortBy === "offers" ? "newest" : sortBy;

      let displayListings: Listing[] = [];
      let newBuffer: Listing[] = [];
      let newLastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      let newHasMore = false;

      // If we have buffered listings and not resetting, use them first
      // Buffer is already sorted (friends first), so we can use it directly
      if (!reset && listingBuffer.length > 0) {
        displayListings = listingBuffer.slice(0, PAGE_SIZE);
        newBuffer = listingBuffer.slice(PAGE_SIZE);
        // If buffer has more, we still have more to show
        newHasMore = newBuffer.length > 0 || hasMore;
        newLastDoc = lastDoc; // Keep existing lastDoc when using buffer
        // Total count remains the same when using buffer
      }

      // If we don't have enough from buffer, fetch more
      if (displayListings.length < PAGE_SIZE) {
        // U praksi jedan batch zna vratiti premalo validnih oglasa (npr. zbog skrivenih/prodatih),
        // pa ovdje pokušamo nekoliko uzastopnih batch-eva da popunimo stranicu i izbjegnemo "zaglavljivanje".
        let fetchCursor: QueryDocumentSnapshot<DocumentData> | null = reset ? null : lastDoc;
        let queryHasMore = true;
        let attempts = 0;
        const maxAttempts = reset ? 1 : 3;
        const fetchLimit = reset ? PAGE_SIZE : FETCH_BATCH_SIZE;
        let allFetched: Listing[] = [];

        while (displayListings.length + allFetched.length < PAGE_SIZE && queryHasMore && attempts < maxAttempts) {
          const result: ListingsQueryResult = await getListingsPaginatedAsync({
            limit: fetchLimit,
            lastDoc: fetchCursor,
            category: filters.category?.trim() || undefined,
            productType: filters.productType?.trim() || undefined,
            brand: filters.brand?.trim() || undefined,
            size: filters.size?.trim() || undefined,
            color: filters.color?.trim() || undefined,
            condition: filters.condition?.trim() || undefined,
            city: filters.city?.trim() || undefined,
            minPrice,
            maxPrice,
            sortBy: sortParam,
            onlyTrade: onlyTrade || undefined,
            onlyOffers: onlyOffers || undefined,
            searchQuery: filters.q?.trim() || undefined,
            excludeSellerId: userIdToExclude,
          });

          allFetched = [...allFetched, ...result.listings];
          fetchCursor = result.lastDoc;
          queryHasMore = result.hasMore;
          attempts += 1;
        }

        // Combine buffer with newly fetched listings
        const allListings = [...displayListings, ...allFetched];

        // Uvijek prikaži PAGE_SIZE, a ostatak ide u buffer (već sortiran)
        displayListings = allListings.slice(0, PAGE_SIZE);
        newBuffer = allListings.slice(PAGE_SIZE);
        newLastDoc = fetchCursor;
        newHasMore = queryHasMore || newBuffer.length > 0;
      }

      setListings((prev) => {
        const nextListings = reset ? displayListings : [...prev, ...displayListings];
        // Sačuvaj trenutne oglase u globalni cache za brži povratak na početnu
        saveListings(nextListings);
        return nextListings;
      });
      setListingBuffer(newBuffer);
      setLastDoc(newLastDoc);
      setHasMore(newHasMore);
    } catch (error) {
      console.error("Error loading listings:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, sortBy, lastDoc, listingBuffer, hasMore]);

  // Load initial data
  useEffect(() => {
    // Fallback: in dev/HMR there are cases where the imported cache getter
    // can momentarily be undefined or not-a-function (Turbopack edge cases).
    const cached =
      typeof getListingsCache === "function" ? getListingsCache() : [];
    const returningFromOglas =
      typeof window !== "undefined" &&
      (sessionStorage.getItem("nosiva_marketplace_scroll") !== null ||
        sessionStorage.getItem("nosiva_marketplace_scroll_listing_id") !== null);

    // Kad se vraća sa oglasa: ostavi listu iz cachea (sve što je već učitano) i samo vrati skrol
    if (returningFromOglas && cached.length > 0) {
      setListings(cached);
      setLoading(false);
      return;
    }

    const hasCache = cached.length > 0;
    if (hasCache) {
      setListings(cached);
      setLoading(false);
    }
    if (process.env.NODE_ENV === "development") {
      seedIfEmpty();
    }
    // Kad imamo cache, učitaj u pozadini bez prikaza loadera
    loadListings(true, hasCache);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const connection = (navigator as Navigator & {
      connection?: {
        saveData?: boolean;
        effectiveType?: string;
        addEventListener?: (type: string, handler: () => void) => void;
        removeEventListener?: (type: string, handler: () => void) => void;
      };
    }).connection;

    const updateMode = () => {
      const mobileViewport = mediaQuery.matches;
      const saveData = Boolean(connection?.saveData);
      const slowConnection =
        typeof connection?.effectiveType === "string" &&
        (connection.effectiveType.includes("2g") ||
          connection.effectiveType.includes("3g"));
      setIsMobilePerfMode(mobileViewport || saveData || slowConnection);
    };

    updateMode();
    mediaQuery.addEventListener("change", updateMode);
    connection?.addEventListener?.("change", updateMode);

    return () => {
      mediaQuery.removeEventListener("change", updateMode);
      connection?.removeEventListener?.("change", updateMode);
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdminUser(false);
        return;
      }
      try {
        const adminStatus = await isAdmin(user.uid);
        setIsAdminUser(adminStatus);
      } catch {
        setIsAdminUser(false);
      }
    });
    return () => unsub();
  }, []);

  // Update total count when we know all listings are loaded (fallback if count query wasn't used)
  useEffect(() => {
    if (!hasMore && totalCount === null) {
      // We've loaded everything, so total is listings + buffer
      // This is a fallback if the count query wasn't executed
      setTotalCount(listings.length + listingBuffer.length);
    }
  }, [hasMore, listings.length, listingBuffer.length, totalCount]);

  // Vrati skrol na isto mjesto kad se korisnik vrati sa stranice oglasa (lista je već iz cachea)
  useEffect(() => {
    if (hasRestoredScrollRef.current || typeof window === "undefined" || listings.length === 0) return;

    const targetId = sessionStorage.getItem("nosiva_marketplace_scroll_listing_id");
    const savedY = sessionStorage.getItem("nosiva_marketplace_scroll");
    const y = savedY !== null ? parseInt(savedY, 10) : 0;
    const scrollY = Number.isFinite(y) && y >= 0 ? y : 0;

    sessionStorage.removeItem("nosiva_marketplace_scroll");
    sessionStorage.removeItem("nosiva_marketplace_scroll_listing_id");
    hasRestoredScrollRef.current = true;

    requestAnimationFrame(() => {
      if (targetId && listings.some((l) => String(l.id) === String(targetId))) {
        const el = document.getElementById(`listing-${targetId}`);
        if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
        else if (scrollY > 0) window.scrollTo(0, scrollY);
      } else if (scrollY > 0) {
        window.scrollTo(0, scrollY);
      }
    });
  }, [listings.length]);

  // Tačan ukupan broj u pozadini (ne blokira prvi load)
  useEffect(() => {
    if (isMobilePerfMode || totalCount !== null || loading || listings.length === 0) return;
    const t = setTimeout(() => {
      const minPriceForCount = filters.min ? Number(filters.min) : undefined;
      const maxPriceForCount = filters.max ? Number(filters.max) : undefined;
      getListingsCountAsync({
        category: filters.category?.trim() || undefined,
        productType: filters.productType?.trim() || undefined,
        brand: filters.brand?.trim() || undefined,
        size: filters.size?.trim() || undefined,
        color: filters.color?.trim() || undefined,
        condition: filters.condition?.trim() || undefined,
        city: filters.city?.trim() || undefined,
        minPrice: minPriceForCount,
        maxPrice: maxPriceForCount,
        onlyTrade: sortBy === "trade" || undefined,
        onlyOffers: sortBy === "offers" || undefined,
        searchQuery: filters.q?.trim() || undefined,
      })
        .then((count) => setTotalCount(count))
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [isMobilePerfMode, loading, listings.length, totalCount, filters, sortBy]);

  // Automatsko učitavanje kad korisnik skrola do dna (ne odmah – da prvi load ostane brz)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [e] = entries;
        if (!e?.isIntersecting || loading || loadingMore) return;
        loadListings(false);
      },
      { rootMargin: "80px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadListings]);

  // Reload when filters or sort changes
  useEffect(() => {
    setListingBuffer([]); // Clear buffer when filters change
    setTotalCount(null); // Reset total count when filters change

    if (isFirstFiltersRef.current) {
      isFirstFiltersRef.current = false;
      return; // Initial load is done by "Load initial data" effect
    }
    loadListings(true);

    const hasAnyFilterOrSort =
      (filters.category && filters.category.trim() !== "") ||
      (filters.productType && filters.productType.trim() !== "") ||
      (filters.brand && filters.brand.trim() !== "") ||
      (filters.size && filters.size.trim() !== "") ||
      (filters.color && filters.color.trim() !== "") ||
      (filters.condition && filters.condition.trim() !== "") ||
      (filters.city && filters.city.trim() !== "") ||
      (filters.min && filters.min.trim() !== "") ||
      (filters.max && filters.max.trim() !== "") ||
      sortBy !== "newest";

    if (!hasAnyFilterOrSort) {
      return;
    }

    const minPriceValue = filters.min ? Number(filters.min) : undefined;
    const maxPriceValue = filters.max ? Number(filters.max) : undefined;

    trackEvent("search_filter", {
      category: filters.category || undefined,
      product_type: filters.productType || undefined,
      brand: filters.brand || undefined,
      size: filters.size || undefined,
      color: filters.color || undefined,
      condition: filters.condition || undefined,
      city: filters.city || undefined,
      min_price:
        typeof minPriceValue === "number" && Number.isFinite(minPriceValue)
          ? minPriceValue
          : undefined,
      max_price:
        typeof maxPriceValue === "number" && Number.isFinite(maxPriceValue)
          ? maxPriceValue
          : undefined,
      sort_by: sortBy,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, filters.productType, filters.brand, filters.size, filters.color, filters.condition, filters.city, filters.min, filters.max, sortBy]);

  // Reload when search query changes (with debounce)
  useEffect(() => {
    // Preskoči prvi render da izbjegnemo dupli upit (inicijalno učitavanje već radi drugi effect)
    if (isFirstSearchRef.current) {
      isFirstSearchRef.current = false;
      return;
    }

    setListingBuffer([]); // Clear buffer when search changes
    setTotalCount(null); // Reset total count when search changes
    const timer = setTimeout(() => {
      loadListings(true);

      const query = String(filters.q || "").trim();
      if (!query) return;

      trackEvent("search_filter", {
        search_query: query,
      });
    }, 300); // 300ms debounce za brži osjećaj pretrage

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q]);

  // For filter options, we still need to load all unique values
  // This is a one-time load for dropdown options
  const [filterOptions, setFilterOptions] = useState({
    brands: [] as string[],
    sizes: [] as string[],
    colors: [] as string[],
    cities: [] as string[],
    types: [] as string[],
  });

  // Postavi početne opcije filtera jednom (ne zavise od trenutno odabranih filtera)
  useEffect(() => {
    setFilterOptions({
      brands: DEFAULT_BRANDS,
      sizes: DEFAULT_SIZES,
      colors: DEFAULT_COLORS,
      cities: CITIES_BIH,
      types: DEFAULT_TYPES,
    });
  }, []);

  // Normalizacija opcija: trim, uklanjanje tačke na kraju; isključi samo "." ili prazne
  const normalizeOption = (s: string) =>
    (s && typeof s === "string" ? s.trim().replace(/\.$/, "") : "");
  const isValidOption = (s: string) => {
    const n = normalizeOption(s);
    return n.length > 0 && n !== ".";
  };

  // Enrichment iz stvarnih oglasa – samo dodajemo nove vrijednosti, nikad ne uklanjamo postojeće
  useEffect(() => {
    if (!listings.length) {
      processedListingsCountRef.current = 0;
      firstListingIdRef.current = "";
      return;
    }

    const firstId = String(listings[0]?.id || "");
    const shouldReprocessAll =
      processedListingsCountRef.current > listings.length ||
      (firstListingIdRef.current !== "" && firstListingIdRef.current !== firstId);
    const startIndex = shouldReprocessAll ? 0 : processedListingsCountRef.current;
    const sourceListings = listings.slice(startIndex);
    if (!sourceListings.length) return;

    const uniq = (arr: string[]) =>
      Array.from(new Set(arr)).filter(Boolean).sort();

    const fromListings = {
      brands: sourceListings.map((x) => normalizeOption(x.brand)).filter(isValidOption),
      sizes: sourceListings.map((x) => normalizeOption(x.size)).filter(isValidOption),
      colors: sourceListings.map((x) => normalizeOption(x.color)).filter(isValidOption),
      cities: sourceListings.map((x) => normalizeOption(x.city)).filter(isValidOption),
      types: sourceListings.map((x) => normalizeOption(x.productType)).filter(isValidOption),
    };

    setFilterOptions((prev) => ({
      brands: uniq([...prev.brands.map(normalizeOption).filter(isValidOption), ...fromListings.brands]),
      sizes: uniq([...prev.sizes.map(normalizeOption).filter(isValidOption), ...fromListings.sizes]),
      colors: uniq([...prev.colors.map(normalizeOption).filter(isValidOption), ...fromListings.colors]),
      cities: CITIES_BIH,
      types: uniq([...prev.types.map(normalizeOption).filter(isValidOption), ...fromListings.types]),
    }));

    processedListingsCountRef.current = listings.length;
    firstListingIdRef.current = firstId;
  }, [listings]);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([k, v]) =>
      k !== "q" ? String(v).trim() !== "" : String(v).trim() !== ""
    );
  }, [filters]);

  // Sortiranje na klijentu da sve opcije rade pouzdano
  const sortedListings = useMemo(() => {
    if (!listings.length) return listings;
    if (sortBy === "newest") return listings;

    if (sortBy === "oldest") {
      const list = [...listings];
      return list.sort((a, b) => a.createdAt - b.createdAt);
    }

    if (sortBy === "price-low" || sortBy === "price-high") {
      const list = [...listings];
      const toNum = (p: unknown): number => {
        if (p == null) return 0;
        if (typeof p === "number") return Number.isFinite(p) ? p : 0;
        if (typeof p === "string") return Number.isFinite(parseFloat(String(p).trim())) ? parseFloat(String(p).trim()) : 0;
        return 0;
      };
      // Sa fiksnom cijenom prvo (sortirano), "po dogovoru" na kraju – da nikad ne bude prazno
      const hasRealPrice = (item: Listing) => {
        const n = toNum(item.price);
        return n > 0 && Number.isFinite(n);
      };
      const isPoDogovoru = (item: Listing) => !hasRealPrice(item);
      const saCijenom = list.filter(hasRealPrice);
      const poDogovoru = list.filter(isPoDogovoru);
      saCijenom.sort((a, b) => {
        const pa = toNum(a.price);
        const pb = toNum(b.price);
        return sortBy === "price-low" ? pa - pb : pb - pa;
      });
      return [...saCijenom, ...poDogovoru];
    }

    if (sortBy === "trade") {
      const list = [...listings];
      const tradeList = list.filter((item) => item.allowsTrade);
      return tradeList.sort((a, b) => b.createdAt - a.createdAt);
    }

    if (sortBy === "offers") {
      const list = [...listings];
      const offersList = list.filter((item) => item.allowsOffers);
      return offersList.sort((a, b) => b.createdAt - a.createdAt);
    }

    return listings;
  }, [listings, sortBy]);

  return (
    <>
      <Header />

      <main className="max-w-6xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-3 sm:py-4 md:py-6 lg:py-10 space-y-3 sm:space-y-4 md:space-y-6 lg:space-y-8">
        {/* Header Section */}
        <div className="space-y-4">
          <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[var(--text)]">
                Oglasi
              </h1>
              <p className="mt-1 sm:mt-2 text-[var(--muted)] text-xs sm:text-sm">
                Pronađi savršen komad odjeće u svojoj okolini
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="relative h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full border border-[var(--border)] bg-[var(--card)] hover:bg-[var(--bg)] transition shadow-sm text-[var(--text)]"
                aria-expanded={filtersOpen}
                aria-controls="filters-panel"
                title="Filteri"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  className="mx-auto sm:w-[18px] sm:h-[18px] md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 5h18" />
                  <path d="M6 12h12" />
                  <path d="M10 19h4" />
                </svg>

                {hasActiveFilters ? (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 h-2 w-2 sm:h-3 sm:w-3 rounded-full bg-[var(--accent)] ring-2 ring-[var(--card)]" />
                ) : null}
              </button>

              <a
                href="/add"
                className="px-2.5 sm:px-3 md:px-4 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 rounded-lg sm:rounded-xl text-white text-xs sm:text-sm md:text-base font-bold bg-[var(--accent)] hover:opacity-90 transition shadow-sm hover:shadow-md flex items-center gap-1 sm:gap-1.5 md:gap-2"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:w-3 sm:h-3 md:w-4 md:h-4 lg:w-[18px] lg:h-[18px]">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span className="hidden sm:inline">Objavi oglas</span>
                <span className="sm:hidden">Objavi</span>
              </a>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-4">
            <div className="bg-[var(--accent)]/10 border-2 border-[var(--accent)]/30 rounded-lg sm:rounded-xl p-1.5 sm:p-2.5 md:p-4 lg:p-5 shadow-sm hover:shadow-md transition hover:border-[var(--accent)]/50">
              <div className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-[var(--accent)] uppercase tracking-wide">Učitano</div>
              <div className="text-[10px] sm:text-xs md:text-sm font-semibold text-[var(--accent)] mt-0.5 sm:mt-1 md:mt-2">
                {loading ? (
                  "Učitava..."
                ) : totalCount !== null ? (
                  `Učitano ${sortedListings.length} od ${totalCount}`
                ) : hasMore ? (
                  `Učitano ${sortedListings.length} od ${sortedListings.length}+`
                ) : (
                  `Učitano ${sortedListings.length} od ${sortedListings.length}`
                )}
              </div>
            </div>
            <div className="bg-[var(--accent)]/10 border-2 border-[var(--accent)]/30 rounded-lg sm:rounded-xl p-1.5 sm:p-2.5 md:p-4 lg:p-5 shadow-sm hover:shadow-md transition hover:border-[var(--accent)]/50">
              <div className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-[var(--accent)] uppercase tracking-wide">Filteri</div>
              <div className="text-base sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-[var(--accent)] mt-0.5 sm:mt-1 md:mt-2">
                {Object.values(filters).filter(v => String(v).trim() !== "").length}
              </div>
            </div>
            <div className="bg-[var(--accent)]/10 border-2 border-[var(--accent)]/30 rounded-lg sm:rounded-xl p-1.5 sm:p-2.5 md:p-4 lg:p-5 shadow-sm hover:shadow-md transition hover:border-[var(--accent)]/50">
              <div className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-[var(--accent)] uppercase tracking-wide">Status</div>
              <div className="text-[10px] sm:text-xs md:text-sm font-semibold text-[var(--accent)] mt-0.5 sm:mt-1 md:mt-2">
                {loading ? "Učitava..." : hasMore ? "Dostupno još oglasa" : "Svi oglasi učitani"}
              </div>
            </div>
          </div>
        </div>

        <div
          id="filters-panel"
          className={[
            "transition-all duration-300",
            filtersOpen
              ? "opacity-100 max-h-[900px]"
              : "opacity-0 max-h-0 overflow-hidden pointer-events-none",
          ].join(" ")}
        >
          <Filters value={filters} onChange={setFilters} options={filterOptions} />
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 md:gap-4">
          <div className="flex items-center gap-2">
            <div className="text-[11px] sm:text-xs md:text-sm text-[var(--muted)]">
              Prikazano <span className="font-semibold text-[var(--text)]">{sortedListings.length}</span> oglasa
            </div>
            {loading && (
              <div className="flex items-center gap-1 text-[10px] sm:text-[11px] md:text-xs text-[var(--muted)]">
                <svg className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span>Filtriram oglase…</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 w-full sm:w-auto">
            <label className="text-[11px] sm:text-xs md:text-sm text-[var(--text)] font-medium whitespace-nowrap flex-shrink-0">Sortiraj:</label>
            {/* Custom dropdown for mobile */}
            <div className="flex-1 sm:hidden min-w-0 max-w-full relative z-50">
              <MobileSortDropdown value={sortBy} onChange={setSortBy} />
            </div>
            {/* Native select for desktop */}
            <div className="hidden sm:block sm:flex-none min-w-0 max-w-full relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 md:px-4 py-2 rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition text-xs md:text-sm appearance-none"
              >
                <option value="newest">Najnovije</option>
                <option value="oldest">Najstarije</option>
                <option value="price-low">Najniža cijena</option>
                <option value="price-high">Najviša cijena</option>
                <option value="trade">Samo zamjena</option>
                <option value="offers">Samo ponuda</option>
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[var(--accent)]/10 border border-[var(--accent)]/20 text-[var(--accent)] text-sm font-medium">
            <svg className="h-5 w-5 animate-spin flex-shrink-0" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Učitavanje filtera i sortiranja…</span>
          </div>
        )}

        {loading && sortedListings.length === 0 ? (
          <ListingGridSkeleton count={12} />
        ) : sortedListings.length === 0 ? (
          <div className="text-center py-20 bg-[var(--card)] rounded-2xl border border-[var(--border)] shadow-sm">
            <svg
              className="mx-auto h-16 w-16 text-[var(--muted)]/50 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-[var(--text)] mb-2">Nema rezultata</h3>
            <p className="text-sm text-[var(--muted)] mb-4">
              Pokušaj sa drugim filterima ili očisti sve filtere
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => setFilters(EMPTY)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-[var(--accent)] hover:bg-[var(--accent)]/10 transition"
              >
                Očisti filtere
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
              {sortedListings.map((item, index) => (
                <div key={item.id} id={`listing-${item.id}`}>
                  <ListingCard
                    item={item}
                    showAdminDelete={isAdminUser}
                    priority={index < 4}
                    position={index + 1}
                  />
                </div>
              ))}
            </div>
            
            {hasMore && (
              <div className="flex flex-col items-center pt-6 pb-4" ref={loadMoreSentinelRef}>
                {loadingMore && (
                  <div className="flex items-center gap-2 text-sm text-[var(--muted)] mb-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Učitavanje...
                  </div>
                )}
                <div className="h-1 w-full min-h-[1px]" aria-hidden />
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}
